/**
 * Web Push subscription from the client side.
 *
 * Three things have to line up: browser permission, a service worker
 * registration, and a subscription the server knows about. Any one of them
 * missing means the user is on the email fallback, which is a working state —
 * not an error to shout about.
 */

import { apiGet, apiPost } from './api.js'

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export function permission() {
  return pushSupported() ? Notification.permission : 'unsupported'
}

/** VAPID keys travel as base64url; the API wants raw bytes. */
function decodeKey(base64url) {
  const padded = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const raw = window.atob(padded)
  const bytes = new Uint8Array(raw.length)

  for (let i = 0; i < raw.length; i += 1) {
    bytes[i] = raw.charCodeAt(i)
  }

  return bytes
}

export async function pushStatus() {
  try {
    return await apiGet('/push/status')
  } catch {
    return { subscriptions: 0, channel: 'email', public_key: '' }
  }
}

/**
 * Asks for permission if needed, subscribes, and registers the endpoint.
 *
 * @returns {Promise<'subscribed'|'denied'|'unsupported'|'unavailable'>}
 */
export async function enablePush() {
  if (!pushSupported()) {
    return 'unsupported'
  }

  const { public_key: publicKey } = await apiGet('/push/key')

  if (!publicKey) {
    // No VAPID keys configured: the server cannot send push at all.
    return 'unavailable'
  }

  if (Notification.permission !== 'granted') {
    const result = await Notification.requestPermission()

    if (result !== 'granted') {
      return 'denied'
    }
  }

  const registration = await navigator.serviceWorker.ready

  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: decodeKey(publicKey),
    }))

  const payload = subscription.toJSON()

  await apiPost('/push/subscribe', {
    endpoint: payload.endpoint,
    keys: payload.keys,
  })

  return 'subscribed'
}

/** Never let a stalled browser API outlast the user's patience. */
function withTimeout(promise, ms, fallback = null) {
  return Promise.race([
    promise,
    new Promise((resolve) => {
      setTimeout(() => resolve(fallback), ms)
    }),
  ])
}

export async function disablePush() {
  let endpoint = ''

  // Clearing it in the browser is best effort. The server decides whether
  // anything is sent, so that call must happen whatever the browser does —
  // and WebKit's pushManager can hang rather than reject, so it is raced.
  try {
    if (pushSupported()) {
      const registration = await withTimeout(navigator.serviceWorker.getRegistration(), 3000)
      const subscription = await withTimeout(registration?.pushManager.getSubscription(), 3000)

      if (subscription) {
        endpoint = subscription.endpoint
        await withTimeout(subscription.unsubscribe(), 3000)
      }
    }
  } catch {
    // Some engines reject pushManager access outright; carry on regardless.
  }

  await apiPost('/push/unsubscribe', { endpoint })
}
